import { isoInstantFromClinicDateAndTime } from "@/lib/clinicTime";

import { apiDelete, apiGet, apiPatch, apiPost } from "./axiosClient";
import axiosClient from "./axiosClient";
import type {
  Appointment,
  AppointmentAuditLog,
  AppointmentReport,
  AppointmentStatus,
  Slot,
} from "@/types/domain";

/**
 * React Query keys — bump the version string when the list response shape changes
 * so stale cache (e.g. raw Spring Page objects) is not reused across deploys.
 */
export const PATIENT_APPTS_QUERY_KEY = ["patient-appts", "v5"] as const;
export const DOCTOR_APPTS_QUERY_KEY = ["doctor-appts", "v5"] as const;

export const appointmentReportsQueryKey = (appointmentId: string) =>
  ["appointment-reports", appointmentId] as const;

function readNonEmptyString(
  o: Record<string, unknown>,
  key: string,
): string | undefined {
  const v = o[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function readIdString(o: Record<string, unknown>, key: string): string | undefined {
  const v = o[key];
  if (typeof v === "string" && v.length > 0) return v;
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return undefined;
}

/** Backend `LocalTime` JSON is usually "HH:mm:ss"; tolerate "HH:mm" or [h,m,s]. */
function normalizeTimePart(v: unknown): string | undefined {
  if (typeof v === "string" && v.length > 0) {
    return v.length === 5 ? `${v}:00` : v;
  }
  if (Array.isArray(v) && v.length >= 2) {
    const h = Number(v[0]);
    const m = Number(v[1]);
    const s = v.length >= 3 ? Number(v[2]) : 0;
    if (!Number.isFinite(h) || !Number.isFinite(m)) return undefined;
    const ss = Number.isFinite(s) ? s : 0;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  }
  return undefined;
}

function readFee(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function mapBackendAppointmentStatus(raw: string): AppointmentStatus {
  const valid: AppointmentStatus[] = [
    "PENDING", "CONFIRMED", "CANCELLED",
    "CANCELLED_BY_PATIENT", "CANCELLED_BY_DOCTOR", "CANCELLED_BY_ADMIN",
    "COMPLETED", "NO_SHOW_PATIENT", "NO_SHOW_DOCTOR", "RESCHEDULED", "EXPIRED",
  ];
  if (valid.includes(raw as AppointmentStatus)) return raw as AppointmentStatus;
  return "PENDING";
}

const OFFSET_ISO_RE = /T.*(Z|[+-]\d{2}:?\d{2})$/;

function looksOffsetIso(s: string | undefined): s is string {
  return typeof s === "string" && OFFSET_ISO_RE.test(s);
}

/**
 * Maps appointment-service `AppointmentDto` into the GUI `Appointment` shape.
 *
 * Preference order for slot start/end instants:
 *  1. `slotStartUtc` / `slotEndUtc` (authoritative UTC instants from the server)
 *  2. legacy `slotStart` / `slotEnd` if already full ISO-8601 with offset
 *  3. derive from clinic-local `slotDate` + `startTime` / `endTime` using the
 *     server-supplied `timeZone` (falls back to VITE_APPOINTMENT_ZONE).
 *
 * Storing UTC instants makes rendering timezone-safe for any viewer.
 */
export function coerceAppointment(raw: unknown): Appointment | null {
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;

  const id = readIdString(o, "id");
  const patientId = readNonEmptyString(o, "patientId");
  const doctorId = readNonEmptyString(o, "doctorId");
  const statusRaw = readNonEmptyString(o, "status") ?? "PENDING";
  const status = mapBackendAppointmentStatus(statusRaw);

  const timeZone = readNonEmptyString(o, "timeZone");

  const utcStart = readNonEmptyString(o, "slotStartUtc");
  const utcEnd = readNonEmptyString(o, "slotEndUtc");

  let slotStart: string | undefined = looksOffsetIso(utcStart)
    ? utcStart
    : undefined;
  let slotEnd: string | undefined = looksOffsetIso(utcEnd) ? utcEnd : undefined;

  if (!slotStart || !slotEnd) {
    const legacyStart = readNonEmptyString(o, "slotStart");
    const legacyEnd = readNonEmptyString(o, "slotEnd");
    if (!slotStart && looksOffsetIso(legacyStart)) slotStart = legacyStart;
    if (!slotEnd && looksOffsetIso(legacyEnd)) slotEnd = legacyEnd;
  }

  if (!slotStart || !slotEnd) {
    const slotDate = readNonEmptyString(o, "slotDate");
    const startTime = normalizeTimePart(o.startTime);
    const endTime = normalizeTimePart(o.endTime);
    if (slotDate && startTime && endTime) {
      slotStart =
        slotStart ?? isoInstantFromClinicDateAndTime(slotDate, startTime, timeZone);
      slotEnd =
        slotEnd ?? isoInstantFromClinicDateAndTime(slotDate, endTime, timeZone);
    }
  }

  if (!id || !patientId || !doctorId || !slotStart || !slotEnd) {
    return null;
  }

  return {
    id,
    doctorId,
    doctorName: readNonEmptyString(o, "doctorName"),
    doctorSpecialization: readNonEmptyString(o, "doctorSpecialization"),
    patientId,
    patientName: readNonEmptyString(o, "patientName"),
    slotStart,
    slotEnd,
    timeZone,
    status,
    consultationFee: readFee(o.consultationFee),
    patientNote: readNonEmptyString(o, "patientNote"),
    doctorRemark: readNonEmptyString(o, "doctorRemark"),
    rescheduledFromId: readIdString(o, "rescheduledFromId"),
    rescheduledToId: readIdString(o, "rescheduledToId"),
    completedAt: readNonEmptyString(o, "completedAt"),
    createdAt: readNonEmptyString(o, "createdAt"),
  };
}

/**
 * Spring `Page<AppointmentDto>` JSON, optional `{ data: … }` wrappers, or a
 * plain array. Always returns an array (never trusts `?? []` on a truthy object).
 */
export function normalizeAppointmentList(
  data: unknown,
  depth = 0,
): Appointment[] {
  if (data == null) return [];
  if (depth > 4) return [];
  if (Array.isArray(data)) {
    return data
      .map((item) => coerceAppointment(item))
      .filter((a): a is Appointment => a != null);
  }
  if (typeof data !== "object" || data === null) return [];

  const o = data as Record<string, unknown>;

  if (Array.isArray(o.content)) {
    return o.content
      .map((item) => coerceAppointment(item))
      .filter((a): a is Appointment => a != null);
  }

  if ("data" in o && o.data !== undefined) {
    return normalizeAppointmentList(o.data, depth + 1);
  }

  const emb = o._embedded;
  if (emb && typeof emb === "object") {
    for (const v of Object.values(emb as Record<string, unknown>)) {
      if (Array.isArray(v)) {
        return v
          .map((item) => coerceAppointment(item))
          .filter((a): a is Appointment => a != null);
      }
    }
  }

  return [];
}

function coerceSlot(raw: unknown, fallbackZone?: string): Slot | null {
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = readIdString(o, "id");
  const doctorId = readNonEmptyString(o, "doctorId");
  const date = readNonEmptyString(o, "date");
  const startTime = normalizeTimePart(o.startTime);
  const endTime = normalizeTimePart(o.endTime);
  const available =
    typeof o.available === "boolean" ? o.available : Boolean(o.available);
  const timeZone = readNonEmptyString(o, "timeZone") ?? fallbackZone ?? "";

  let startUtc = readNonEmptyString(o, "startUtc");
  let endUtc = readNonEmptyString(o, "endUtc");
  if (!looksOffsetIso(startUtc) || !looksOffsetIso(endUtc)) {
    if (date && startTime && endTime) {
      startUtc = isoInstantFromClinicDateAndTime(date, startTime, timeZone);
      endUtc = isoInstantFromClinicDateAndTime(date, endTime, timeZone);
    }
  }

  if (!id || !doctorId || !date || !startTime || !endTime || !startUtc || !endUtc) {
    return null;
  }

  return {
    id,
    doctorId,
    date,
    startTime,
    endTime,
    startUtc,
    endUtc,
    timeZone,
    available,
  };
}

/** Public. 15-min slots for a doctor on a given date (yyyy-MM-dd). */
export const listSlots = async (
  doctorKeycloakId: string,
  date: string,
): Promise<Slot[]> => {
  const data = await apiGet<unknown>(
    `/api/appointments/slots/${doctorKeycloakId}`,
    { params: { date } },
  );
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => coerceSlot(item))
    .filter((s): s is Slot => s != null);
};

export const bookAppointment = async (body: {
  doctorKeycloakId: string;
  slotId: string; // UUID of the selected 15-min slot
  patientNote?: string;
}): Promise<Appointment> => {
  const data = await apiPost<unknown>("/api/appointments/book", body);
  const appt = coerceAppointment(data);
  if (!appt) {
    throw new Error("Unexpected appointment response from server.");
  }
  return appt;
};

export const listMyAppointmentsPatient = async () => {
  const data = await apiGet<unknown>("/api/appointments/patient/mine");
  return normalizeAppointmentList(data);
};

export const listMyAppointmentsDoctor = async () => {
  const data = await apiGet<unknown>("/api/appointments/doctor/mine");
  return normalizeAppointmentList(data);
};

export const cancelAppointment = (id: string) =>
  apiPatch<Appointment>(`/api/appointments/${id}/cancel`);

export const rescheduleAppointment = async (
  appointmentId: string,
  newSlotId: string,
): Promise<Appointment> => {
  const data = await apiPost<unknown>(`/api/appointments/${appointmentId}/reschedule`, {
    newSlotId,
  });
  const appt = coerceAppointment(data);
  if (!appt) throw new Error("Unexpected response from reschedule.");
  return appt;
};

export const completeConsultation = async (
  appointmentId: string,
  doctorRemark: string,
): Promise<Appointment> => {
  const data = await apiPost<unknown>(`/api/appointments/${appointmentId}/complete`, {
    doctorRemark,
  });
  const appt = coerceAppointment(data);
  if (!appt) throw new Error("Unexpected response from complete consultation.");
  return appt;
};

export const ADMIN_APPTS_QUERY_KEY = ["admin-appts", "v1"] as const;

export interface AdminAppointmentFilters {
  doctorId?: string;
  patientId?: string;
  status?: AppointmentStatus;
  bookedDateFrom?: string;
  bookedDateTo?: string;
  apptDateFrom?: string;
  apptDateTo?: string;
  page?: number;
  size?: number;
}

export interface PagedResult<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export const listAppointmentsAdmin = async (
  filters: AdminAppointmentFilters,
): Promise<PagedResult<Appointment>> => {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v != null && v !== ""),
  );
  const data = await apiGet<unknown>("/api/appointments/admin/all", { params });
  const o = data as Record<string, unknown>;
  const content = Array.isArray(o.content) ? o.content : [];
  return {
    content: content
      .map((item) => coerceAppointment(item))
      .filter((a): a is Appointment => a != null),
    totalElements: (o.totalElements as number) ?? 0,
    totalPages: (o.totalPages as number) ?? 0,
    number: (o.number as number) ?? 0,
    size: (o.size as number) ?? 20,
  };
};

export const getAppointmentAuditLog = async (
  appointmentId: string,
): Promise<AppointmentAuditLog[]> => {
  const data = await apiGet<unknown>(`/api/appointments/admin/${appointmentId}/audit-log`);
  if (!Array.isArray(data)) return [];
  return data as AppointmentAuditLog[];
};

export function coerceAppointmentReport(raw: unknown): AppointmentReport | null {
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = readIdString(o, "id");
  const appointmentId = readIdString(o, "appointmentId");
  const fileName =
    readNonEmptyString(o, "originalFilename") ?? readNonEmptyString(o, "fileName");
  if (!id || !appointmentId || !fileName) return null;
  return {
    id,
    appointmentId,
    fileName,
    attachmentNote: readNonEmptyString(o, "attachmentNote"),
    contentType: readNonEmptyString(o, "contentType"),
    sizeBytes: typeof o.sizeBytes === "number" ? o.sizeBytes : undefined,
    uploadedAt: readNonEmptyString(o, "createdAt") ?? readNonEmptyString(o, "uploadedAt"),
  };
}

export async function listReports(appointmentId: string): Promise<AppointmentReport[]> {
  const data = await apiGet<unknown>(`/api/appointments/${appointmentId}/reports`);
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => coerceAppointmentReport(item))
    .filter((r): r is AppointmentReport => r != null);
}

/** Max documents per appointment (must match appointment-service `max-files-per-appointment`). */
export const MAX_APPOINTMENT_REPORT_FILES = 10;

/** One file per request; optional `attachmentNote` for this file. */
export async function uploadAppointmentReport(
  appointmentId: string,
  file: File,
  attachmentNote?: string,
): Promise<AppointmentReport> {
  const fd = new FormData();
  fd.append("file", file);
  const trimmed = attachmentNote?.trim();
  if (trimmed) fd.append("attachmentNote", trimmed);
  const { data } = await axiosClient.post<unknown>(`/api/appointments/${appointmentId}/reports`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const r = coerceAppointmentReport(data);
  if (!r) throw new Error("Unexpected report response from server.");
  return r;
}

export const updatePatientNote = (appointmentId: string, patientNote: string) =>
  apiPatch<unknown>(`/api/appointments/${appointmentId}/patient-note`, { patientNote });

export const deleteAppointmentReport = (appointmentId: string, reportId: string) =>
  apiDelete<void>(`/api/appointments/${appointmentId}/reports/${reportId}`);

/** Authenticated download (Bearer). */
export async function downloadReportFile(
  appointmentId: string,
  reportId: string,
  downloadFilename: string,
): Promise<void> {
  const res = await axiosClient.get(`/api/appointments/${appointmentId}/reports/${reportId}/file`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = downloadFilename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export const reportFileUrl = (appointmentId: string, reportId: string) =>
  `/api/appointments/${appointmentId}/reports/${reportId}/file`;

// ── Prescription API ──────────────────────────────────────────────────

import type { Prescription, PrescriptionVerification } from "@/types/domain";

export interface CreatePrescriptionBody {
  diagnosis: string;
  diagnosticReferral?: string;
  remarks?: string;
  medications: {
    drugName: string;
    dosage?: string;
    frequency?: string;
    duration?: string;
    instructions?: string;
  }[];
}

export const PRESCRIPTION_QUERY_KEY = (appointmentId: string) =>
  ["prescription", appointmentId] as const;

export const createPrescription = async (
  appointmentId: string,
  body: CreatePrescriptionBody,
): Promise<Prescription> => {
  const data = await apiPost<Prescription>(
    `/api/appointments/${appointmentId}/prescription`,
    body,
  );
  return data;
};

export const getPrescription = async (
  appointmentId: string,
): Promise<Prescription> => {
  const data = await apiGet<Prescription>(
    `/api/appointments/${appointmentId}/prescription`,
  );
  return data;
};

export async function downloadPrescriptionPdf(
  appointmentId: string,
  filename?: string,
): Promise<void> {
  const res = await axiosClient.get(
    `/api/appointments/${appointmentId}/prescription/pdf`,
    { responseType: "blob" },
  );
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? "prescription.pdf";
  a.click();
  window.URL.revokeObjectURL(url);
}

export const verifyPrescription = async (
  code: string,
): Promise<PrescriptionVerification> => {
  const data = await apiGet<PrescriptionVerification>(
    `/api/appointments/prescriptions/${code}/verify`,
  );
  return data;
};
